import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, User, Lock, Palette, LifeBuoy, ExternalLink, Sun, Moon, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Card = ({ icon: Icon, title, desc, children, testId }) => (
    <section data-testid={testId} className="card-soft p-6">
        <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                <Icon size={18} />
            </div>
            <div className="flex-1">
                <h2 className="font-heading font-semibold text-lg text-foreground">{title}</h2>
                {desc && <p className="text-sm text-muted-foreground mt-1">{desc}</p>}
            </div>
        </div>
        <div className="mt-5">{children}</div>
    </section>
);

const Settings = () => {
    const { user, refresh } = useAuth();
    const [name, setName] = useState(user?.name || "");
    const [companyName, setCompanyName] = useState(user?.company_name || "");
    const [saving, setSaving] = useState(false);
    const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
    const [pwdSaving, setPwdSaving] = useState(false);
    const [theme, setTheme] = useState(() =>
        typeof window !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light",
    );

    useEffect(() => {
        if (user) {
            setName(user.name || "");
            setCompanyName(user.company_name || "");
        }
    }, [user]);

    if (!user) {
        return (
            <div className="container-page py-24 text-center">
                <User size={26} className="mx-auto text-emerald-500" />
                <h1 className="mt-4 font-heading text-2xl font-medium">Please log in to view settings.</h1>
                <Link to="/login" className="btn-green mt-6 inline-flex">Log in</Link>
            </div>
        );
    }

    const saveProfile = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            await api.patch("/me", { name, company_name: companyName || null });
            toast.success("Profile updated.");
            refresh?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const savePassword = async (e) => {
        e.preventDefault();
        if (pwd.next !== pwd.confirm) return toast.error("Passwords don't match.");
        if (pwd.next.length < 8) return toast.error("New password must be at least 8 characters.");
        try {
            setPwdSaving(true);
            await api.post("/me/password", { current_password: pwd.current, new_password: pwd.next });
            toast.success("Password updated.");
            setPwd({ current: "", next: "", confirm: "" });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setPwdSaving(false); }
    };

    const setThemeMode = (mode) => {
        setTheme(mode);
        if (mode === "dark") document.documentElement.classList.add("dark");
        else document.documentElement.classList.remove("dark");
        try { localStorage.setItem("simonara-theme", mode); } catch { /* ignore */ }
    };

    return (
        <div className="container-page py-12 max-w-4xl" data-testid="settings-page">
            <div className="mb-10">
                <p className="overline">Preferences</p>
                <h1 className="mt-2 font-heading font-medium text-3xl lg:text-4xl tracking-tight text-foreground">Settings</h1>
            </div>

            <div className="space-y-6">
                <Card icon={User} title="Profile" desc="How you appear across Simonara." testId="settings-profile">
                    <form onSubmit={saveProfile} className="space-y-4">
                        <div>
                            <Label>Full name</Label>
                            <Input data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-2" />
                        </div>
                        <div>
                            <Label>Email</Label>
                            <div className="mt-2 flex gap-2">
                                <Input value={user.email} disabled className="opacity-60 flex-1" />
                                <button
                                    type="button"
                                    onClick={async () => {
                                        const ne = window.prompt("New email address — a confirmation link will be sent to it. You'll be signed out on all devices once you confirm.", "");
                                        if (!ne) return;
                                        try {
                                            await api.post("/auth/email-change/request", { new_email: ne.trim() });
                                            toast.success("If that email is available, we sent a confirmation link to it.");
                                        } catch (err) {
                                            toast.error(err?.response?.data?.detail || "Couldn't request the change.");
                                        }
                                    }}
                                    data-testid="settings-change-email"
                                    className="btn-outline text-xs px-3"
                                >
                                    Change email
                                </button>
                            </div>
                        </div>
                        {user.role === "employer" && (
                            <div>
                                <Label>Company name</Label>
                                <Input data-testid="settings-company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="mt-2" />
                            </div>
                        )}
                        <div className="flex items-center justify-between pt-2">
                            <Badge variant="outline" className="text-xs">{user.role} · {user.auth_provider || "email"}</Badge>
                            <button type="submit" disabled={saving} data-testid="settings-save-profile" className="btn-green disabled:opacity-60">
                                {saving ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving</> : <><Save size={14} className="mr-2" /> Save changes</>}
                            </button>
                        </div>
                    </form>
                </Card>

                {user.auth_provider !== "google" && (
                    <Card icon={Lock} title="Password" desc="Change your password. Minimum 8 characters." testId="settings-password">
                        <form onSubmit={savePassword} className="space-y-4">
                            <div>
                                <Label>Current password</Label>
                                <Input type="password" data-testid="settings-current-password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} required className="mt-2" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label>New password</Label>
                                    <Input type="password" data-testid="settings-new-password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} required className="mt-2" />
                                </div>
                                <div>
                                    <Label>Confirm new password</Label>
                                    <Input type="password" data-testid="settings-confirm-password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} required className="mt-2" />
                                </div>
                            </div>
                            <div className="flex justify-end pt-2">
                                <button type="submit" disabled={pwdSaving} data-testid="settings-save-password" className="btn-green disabled:opacity-60">
                                    {pwdSaving ? <><Loader2 size={14} className="animate-spin mr-2" /> Updating</> : "Update password"}
                                </button>
                            </div>
                        </form>
                    </Card>
                )}

                <Card icon={Palette} title="Appearance" desc="Light or dark. Your choice syncs across your devices." testId="settings-appearance">
                    <div className="grid grid-cols-2 gap-3 max-w-sm">
                        <button
                            data-testid="theme-light"
                            onClick={() => setThemeMode("light")}
                            className={`p-4 rounded-xl border-2 text-left transition ${theme === "light" ? "border-brand-green bg-brand-green/5" : "border-border hover:border-brand-navy"}`}
                        >
                            <Sun size={16} className="text-amber-500" />
                            <div className="font-semibold text-sm mt-2 text-foreground">Light</div>
                        </button>
                        <button
                            data-testid="theme-dark"
                            onClick={() => setThemeMode("dark")}
                            className={`p-4 rounded-xl border-2 text-left transition ${theme === "dark" ? "border-brand-green bg-brand-green/5" : "border-border hover:border-brand-navy"}`}
                        >
                            <Moon size={16} className="text-indigo-400" />
                            <div className="font-semibold text-sm mt-2 text-foreground">Dark</div>
                        </button>
                    </div>
                </Card>

                <Card icon={LifeBuoy} title="Support" desc="Need a hand? We reply within one business day." testId="settings-support">
                    <div className="flex flex-wrap items-center gap-3">
                        <Link to="/contact" className="btn-outline inline-flex text-sm">Contact support <ExternalLink size={12} className="ml-2" /></Link>
                        <Link to="/resources#help" className="btn-outline inline-flex text-sm">Browse Help Center <ExternalLink size={12} className="ml-2" /></Link>
                        <Link to="/resources#faqs" className="btn-outline inline-flex text-sm">Read FAQs <ExternalLink size={12} className="ml-2" /></Link>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default Settings;
